import React, { useState, useRef } from "react";
import styled from "styled-components";
import BodySmall from "../typography/BodySmall";
import { HorizontalSpacer } from "../Spacers";
import Body from "../typography/Body";
import { theme } from "../../../theme";
import { ChevronSmallDownIcon } from "../icons/ChevronIcons";
import Small from "../typography/Small";

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const InputStyle = styled.input`
  height: 36px;
  border: none;
  border-bottom: 1.25px solid ${theme.colors.grey40};
  font-size: 14px;
  font-weight: 500;
  color: ${theme.colors.text};
  padding: 0 36px 0 4px;
  background-color: ${theme.colors.white};
  min-width: 120px;
  width: 100%;
  box-sizing: border-box;

  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    color 0.2s ease;

  &:hover {
    cursor: pointer;
    ${(props) =>
      !props.disabled &&
      `
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05); 
    `}
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.08);
  }
`;

const Dropdown = styled.ul<{ $openUpward: boolean; $maxHeight: string }>`
  position: absolute;
  width: 100%;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.textMuted};
  border-radius: 4px;
  max-height: ${(props) => props.$maxHeight};
  overflow-y: auto;
  padding: 0;
  list-style: none;
  z-index: 100;
  box-sizing: border-box;
  ${(props) =>
    props.$openUpward
      ? `
    bottom: 100%;
    margin-bottom: 4px;
  `
      : `
    top: 100%;
    margin-top: 4px;
  `}
`;

const DropdownItem = styled.li`
  padding: 8px;
  cursor: pointer;
  color: ${theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  &:hover {
    background-color: ${theme.colors.hover};
  }
`;

const DropdownHeader = styled.li`
  padding: 4px 8px 2px;
  pointer-events: none;
  user-select: none;
`;

const ChevronWrapper = styled.div<{ open: boolean }>`
  position: absolute;
  right: 12px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 50%;
  transform: translateY(-50%) rotate(${(p) => (p.open ? "180deg" : "0deg")});
  transition: transform 0.18s ease;
  color: ${theme.colors.textMuted};
  pointer-events: auto;
  cursor: pointer;
`;

type Option = {
  label: string;
  value: any;
  isHeader?: boolean;
  searchLabel?: string;
  selectValue?: any;
};

interface Props {
  label?: string;
  value: any;
  errorMessage?: string;
  onChange: (value: any) => void;
  disabled?: boolean;
  options: Array<Option>;
  color?: string;
}

const AutocompleteSelect: React.FC<Props> = ({
  label,
  value,
  errorMessage,
  onChange,
  disabled,
  options,
  color = theme.colors.text,
}) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [errorValue, setErrorValue] = useState("");
  const justSelectedRef = useRef(false);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    openUpward: boolean;
    maxHeight: string;
  }>({ openUpward: false, maxHeight: "600px" });

  const optionsKey = options.map((o) => `${o.value}§${o.label}`).join("|");

  React.useEffect(() => {
    setFilteredOptions(options);
    const matchedOption = options.find(
      (opt) => !opt.isHeader && opt.value === value,
    );
    if (!isEditing) {
      setInputValue(matchedOption ? matchedOption.label : "");
    }
    if (matchedOption) setErrorValue("");
    // options is intentionally excluded in favour of optionsKey (stable string representation)
    // isEditing is intentionally excluded to avoid resetting input while the user is typing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, optionsKey]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;

    setIsEditing(true);
    setInputValue(query);

    if (!query) {
      setFilteredOptions(options);
      setIsDropdownOpen(true);
      return;
    }

    const result: typeof options = [];
    let currentHeader: (typeof options)[0] | null = null;
    let groupMatches: typeof options = [];

    const flushGroup = () => {
      if (groupMatches.length > 0) {
        if (currentHeader) result.push(currentHeader);
        result.push(...groupMatches);
      }
      groupMatches = [];
    };

    for (const option of options) {
      if (option.isHeader) {
        flushGroup();
        currentHeader = option;
      } else {
        const searchText = (option.searchLabel || option.label).toLowerCase();
        if (searchText.includes(query.toLowerCase())) {
          groupMatches.push(option);
        }
      }
    }
    flushGroup();

    setFilteredOptions(result);
    setIsDropdownOpen(true);
  };

  const handleOptionSelect = (selectedOption: Option) => {
    if (selectedOption.isHeader) return;
    justSelectedRef.current = true;
    setIsEditing(false);
    setInputValue(selectedOption.label);
    onChange(selectedOption.selectValue ?? selectedOption.value);
    setIsDropdownOpen(false);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const next = e.relatedTarget as Node | null;

    if (next && rootRef.current?.contains(next)) {
      return;
    }

    setIsEditing(false);

    const matchedOption = options.find(
      (opt) => !opt.isHeader && opt.value === value,
    );

    if (matchedOption) {
      setInputValue(matchedOption.label);
      setErrorValue("");
    } else {
      setErrorValue("Ogiltigt val");
    }

    setIsDropdownOpen(false);
  };

  const calculateDropdownPosition = () => {
    if (!rootRef.current) return;

    const rect = rootRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    const minDropdownHeight = 200;
    const maxDropdownHeight = 600;

    // Prefer opening downward if there's enough space
    if (spaceBelow >= minDropdownHeight) {
      setDropdownPosition({
        openUpward: false,
        maxHeight: `${Math.min(spaceBelow - 20, maxDropdownHeight)}px`,
      });
    } else if (spaceAbove >= minDropdownHeight) {
      setDropdownPosition({
        openUpward: true,
        maxHeight: `${Math.min(spaceAbove - 20, maxDropdownHeight)}px`,
      });
    } else {
      // Not enough space in either direction, use the larger space
      if (spaceBelow > spaceAbove) {
        setDropdownPosition({
          openUpward: false,
          maxHeight: `${spaceBelow - 20}px`,
        });
      } else {
        setDropdownPosition({
          openUpward: true,
          maxHeight: `${spaceAbove - 20}px`,
        });
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab") {
      setIsDropdownOpen(false);
      setIsEditing(false);
    }
  };

  const handleClick = () => {
    calculateDropdownPosition();
    setIsDropdownOpen(true);
  };

  const handleFocus = () => {
    setIsEditing(true);

    // select entire text so typing replaces it
    requestAnimationFrame(() => {
      inputRef.current?.select();
    });
  };

  return (
    <div style={{ position: "relative", width: "100%" }} ref={rootRef}>
      {label && (
        <div>
          <Small color={disabled ? theme.colors.textSecondary : color}>
            {label}
          </Small>
          <HorizontalSpacer spacing={0.25} />
        </div>
      )}
      {disabled ? (
        <Body color={color}>{value}</Body>
      ) : (
        <div>
          <InputWrapper>
            <InputStyle
              value={inputValue}
              onChange={handleInputChange}
              onFocus={handleFocus}
              onClick={handleClick}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              ref={inputRef}
            />
            <ChevronWrapper
              open={isDropdownOpen}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDropdownOpen((o) => {
                  const next = !o;
                  if (next) {
                    setIsEditing(true);
                    calculateDropdownPosition();
                    requestAnimationFrame(() => {
                      inputRef.current?.focus();
                      inputRef.current?.select();
                    });
                  }
                  return next;
                });
              }}
            >
              <ChevronSmallDownIcon />
            </ChevronWrapper>
          </InputWrapper>
          {isDropdownOpen && filteredOptions.length > 0 && (
            <Dropdown
              $openUpward={dropdownPosition.openUpward}
              $maxHeight={dropdownPosition.maxHeight}
            >
              {filteredOptions.map((option, index) =>
                option.isHeader ? (
                  <DropdownHeader key={`header-${index}`}>
                    <Small color={theme.colors.textMuted}>{option.label}</Small>
                  </DropdownHeader>
                ) : (
                  <DropdownItem
                    key={`${option.value}-${index}`}
                    onMouseDown={() => handleOptionSelect(option)}
                  >
                    <BodySmall>{option.label}</BodySmall>
                  </DropdownItem>
                ),
              )}
            </Dropdown>
          )}
        </div>
      )}
      {!disabled && (errorMessage || errorValue) && (
        <BodySmall color={theme.colors.error}>{errorMessage}</BodySmall>
      )}
    </div>
  );
};

export default AutocompleteSelect;
